import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { useMe } from '../../lib/queries'
import { MarkIcon, MenuIcon } from '../icons'
import './AppShell.css'

/**
 * A slot in the app bar that a nested route can fill — the wizard's
 * "Saved 4:12 pm" indicator, and nothing else so far. It is a live region, so
 * autosave is announced without a spinner and without a toast.
 */
interface AppBarSlotValue {
  status: ReactNode
  setStatus: (node: ReactNode) => void
}

const AppBarSlotContext = createContext<AppBarSlotValue | null>(null)

export function useAppBarStatus(node: ReactNode, deps: unknown[]): void {
  const slot = useContext(AppBarSlotContext)
  const setStatus = slot?.setStatus
  useEffect(() => {
    setStatus?.(node)
    return () => setStatus?.(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setStatus, ...deps])
}

export function SkipLink() {
  return (
    <a className="skip-link" href="#main">
      Skip to main content
    </a>
  )
}

/**
 * The Help link sits in the same footer position on every page, including the
 * signed-out ones — WCAG 2.2 SC 3.2.6 Consistent Help.
 */
export function Footer({ audience = 'citizen' }: { audience?: Audience }) {
  return (
    <footer className="footer">
      <div className="footer__inner">
        <Link to="/help">Help</Link>
        <span>
          {audience === 'notary'
            ? 'Applicant details are confidential to the job they belong to.'
            : 'Your living will is private to you.'}
        </span>
        <span className="footer__version">Version {__GIT_SHA__}</span>
      </div>
    </footer>
  )
}

/**
 * Which audience the shell is rendering for.
 *
 * `notary` exists so the bar can skip `GET /me` entirely: that call provisions
 * a citizen `users` row for whoever makes it, and a notary must not get one
 * just by loading a page.
 */
export type Audience = 'citizen' | 'notary'

function AppBar({ status, audience }: { status: ReactNode; audience: Audience }) {
  const { signOut, email } = useAuth()
  const isNotary = audience === 'notary'
  const { data: me } = useMe({ enabled: !isNotary })
  const [navOpen, setNavOpen] = useState(false)
  const location = useLocation()

  useEffect(() => setNavOpen(false), [location.pathname])

  const isAdmin = me?.is_admin ?? false
  const home = isNotary ? '/notary' : isAdmin ? '/admin/jobs' : '/'

  return (
    <header className="appbar">
      <div className="appbar__inner">
        <Link className="appbar__brand" to={home}>
          <MarkIcon aria-hidden="true" focusable="false" />
          Living Will
        </Link>

        <button
          className="btn btn--ghost btn--icon appbar__toggle"
          type="button"
          aria-expanded={navOpen}
          aria-controls="appbar-nav"
          onClick={() => setNavOpen((open) => !open)}
        >
          <MenuIcon aria-hidden="true" focusable="false" />
          <span className="u-visually-hidden">{navOpen ? 'Hide menu' : 'Show menu'}</span>
        </button>

        <nav
          className={`appbar__nav appbar__nav--sheet${navOpen ? ' is-open' : ''}`}
          id="appbar-nav"
          aria-label="Main"
        >
          {isNotary ? (
            <>
              <NavLink className="appbar__link" to="/notary" end>
                Claim a job
              </NavLink>
              <NavLink className="appbar__link" to="/notary/jobs">
                My jobs
              </NavLink>
            </>
          ) : isAdmin ? (
            <>
              <NavLink className="appbar__link" to="/admin/jobs">
                Jobs
              </NavLink>
              <NavLink className="appbar__link" to="/admin/notaries">
                Notaries
              </NavLink>
              <NavLink className="appbar__link" to="/admin/templates">
                Templates
              </NavLink>
              <NavLink className="appbar__link" to="/admin/interest">
                Interest
              </NavLink>
            </>
          ) : (
            <>
              <NavLink className="appbar__link" to="/" end>
                Home
              </NavLink>
              <NavLink className="appbar__link" to="/my-living-wills">
                My living wills
              </NavLink>
              <NavLink className="appbar__link" to="/account">
                Account
              </NavLink>
            </>
          )}
        </nav>

        <div className="appbar__actions">
          {status ? (
            <span className="appbar__status u-caption" aria-live="polite">
              {status}
            </span>
          ) : null}
          <button className="btn btn--ghost btn--sm" type="button" onClick={() => void signOut()}>
            Sign out
            {email ? <span className="u-visually-hidden"> ({email})</span> : null}
          </button>
        </div>
      </div>
    </header>
  )
}

/**
 * The authenticated shell. `#main` is the skip-link target and the only
 * `<main>` on the page.
 */
export function AppShell({
  children,
  audience = 'citizen',
}: {
  children: ReactNode
  audience?: Audience
}) {
  const [status, setStatus] = useState<ReactNode>(null)
  const value = useMemo(() => ({ status, setStatus }), [status])

  return (
    <AppBarSlotContext.Provider value={value}>
      <div className="app">
        <SkipLink />
        <AppBar status={status} audience={audience} />
        <main className="page" id="main" tabIndex={-1}>
          {children}
        </main>
        <Footer audience={audience} />
      </div>
    </AppBarSlotContext.Provider>
  )
}
